import React from 'react';

import type { CustomStyle } from '@/app/[locale]/(default-layout)/_components/CustomStyles';

import { WebsiteStyle } from '@cineamo/api/models/website-configuration/WebsiteStyle.types';

import { getPageBackgroundStyle } from '@/utils/page-background';

/**
 * The colour layer of the page background. Fixed and viewport-sized, so it stays put while the
 * page scrolls and the gradient always spans the viewport.
 */
export function PageBackgroundColorLayer({ style }: { style: WebsiteStyle }): React.JSX.Element {
    return (
        <div
            className="fixed left-0 top-0 w-screen h-dvh"
            style={getPageBackgroundStyle(style)}
        />
    );
}

/**
 * The cinema's background image on top of the colour layer, at its configured opacity. Renders
 * nothing unless a background image is configured.
 */
export function PageBackgroundImageLayer({ customStyle }: { customStyle: CustomStyle }): React.JSX.Element {
    if (!customStyle?.backgroundImage) {
        return null;
    }

    return (
        <div
            className="fixed left-0 top-0 w-screen h-screen"
            style={{
                background: customStyle.background
            }}>
            <div
                className="relative w-full h-full"
                style={{
                    backgroundImage: `url(${customStyle.backgroundImage})`,
                    backgroundRepeat: customStyle.backgroundRepeat,
                    backgroundSize: customStyle.backgroundSize,
                    backgroundPosition: customStyle.backgroundPosition ?? 'center',
                    opacity: customStyle.opacity
                }}
            />
        </div>
    );
}

/**
 * The page background as a whole — the same two layers the layouts render, in the same order.
 *
 * Rendered inside a container that clips its subtree, this reproduces the page background within
 * that container: both layers are fixed and viewport-sized, so the strip showing through the
 * container continues the page background seamlessly, wherever the container currently sits. The
 * sticky date headers of the compact program list use it that way to hide the rows scrolling
 * behind them without an opaque colour of their own.
 */
export function PageBackgroundLayers({
    style,
    customStyle
}: {
    style: WebsiteStyle;
    customStyle: CustomStyle;
}): React.JSX.Element {
    return (
        <>
            <PageBackgroundColorLayer style={style} />
            <PageBackgroundImageLayer customStyle={customStyle} />
        </>
    );
}
